/**
 * Kiosk State Utilities
 *
 * This file provides kiosk-specific state types and a wrapper around
 * the unified event state logic for backward compatibility.
 */

import type { components } from "../../apis/gen";
import {
	determineEventState,
	EVENT_STATES,
	mapToKioskState,
} from "../../utils/event-state";

type Event = components["schemas"]["Event"];

// =============================================================================
// Kiosk-specific Stage Types (for backward compatibility)
// =============================================================================

export type KioskStage = "idle" | "waiting" | "playing" | "endgame";

export type KioskSubStage =
	| "normal"
	| "imminent"
	| "checkin"
	| "regular"
	| "ending" // New: for endgame countdown (10s before session end)
	| "new_shot"
	| "trigger_anim"
	| "processing"
	| "reveal"
	| "stats"
	| "all_processed"
	| null;

export interface DeterminedState {
	stage: KioskStage;
	subStage: KioskSubStage;
	sessionIdx?: number;
	secondsLeft?: number;
	requiresCheckIn?: boolean;
	isEventEnded?: boolean;
}

// =============================================================================
// Main State Determination Function
// =============================================================================

/**
 * Determines the kiosk state based on event data and current time.
 * Uses the unified event state logic and maps to kiosk stage/subStage format.
 *
 * @param event - The event data object
 * @param currentTimestamp - Current time in milliseconds (defaults to Date.now())
 * @returns DeterminedState with stage, subStage, and helper values
 */
export const determineKioskState = (
	event: Event | null,
	currentTimestamp: number = Date.now(),
): DeterminedState => {
	// Use unified state determination
	const unifiedState = determineEventState(event, currentTimestamp);

	// Map to kiosk stage/subStage
	const { stage, subStage } = mapToKioskState(unifiedState.state);

	// Handle special case for endgame stats - use "all_processed" for backward compat
	let finalSubStage: KioskSubStage = subStage as KioskSubStage;
	if (unifiedState.state === EVENT_STATES.ENDGAME_STATS) {
		finalSubStage = "all_processed";
	} else if (unifiedState.state === EVENT_STATES.ENDGAME_PROCESSING) {
		finalSubStage = "processing";
	}

	return {
		stage,
		subStage: finalSubStage,
		sessionIdx:
			unifiedState.sessionIdx >= 0 ? unifiedState.sessionIdx : undefined,
		secondsLeft:
			unifiedState.countdown > 0 ? unifiedState.countdown : undefined,
		requiresCheckIn: unifiedState.requiresCheckIn,
		isEventEnded: unifiedState.isEventEnded,
	};
};

// =============================================================================
// Re-exports for convenience
// =============================================================================

export { EVENT_STATES } from "../../utils/event-state";
